import { assertNever } from '@spiaggeit/spit-core'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDebounceCallback } from 'usehooks-ts'

import { useAppDispatch, useAppSelector } from '@/hooks/store'
import { QuoteSetupItems } from '@/models/cart'
import { MapSetupItemKeyName } from '@/models/map'
import { QuoteReservation } from '@/models/quote'
import { cartSlice } from '@/store/cartSlice'
import { cartSliceInitialState } from '@/store/cartSlice/initialState.ts'
import { activeElementQuotedSeatsAmountSelector } from '@/store/cartSlice/selectors/activeElementQuotedSeatsAmountSelector'
import { areDrawerButtonsEnabledSelector } from '@/store/cartSlice/selectors/areDrawerButtonsEnabledSelector'
import { selectActiveCartReservation } from '@/store/cartSlice/selectors/selectActiveCartReservation.ts'
import { cartAddReservationToQuote } from '@/store/cartSlice/thunks/cartAddReservationToQuote.ts'
import { dayDifferenceSelector } from '@/store/insertPeriodSlice'
import {
  mapCurrentSectorDetailsSelector,
  mapSelectedElementServices,
  mapSelectedElementSetupDataSelector,
  mapSelectors,
  mapSlice,
} from '@/store/mapSlice'
import { ticketSlice } from '@/store/ticketSlice'
import { getObjectsComparison } from '@/utils/getObjectsComparison'
import { getSeatDefaultQuantity } from '@/utils/getSeatDefaultQuantity'
import { getServiceUpdatedQuantity } from '@/utils/getServiceUpdatedQuantity'
import { getDaysFromPriceMode, getTimeScheduleKey } from '@/utils/timeSchedules'
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from '../Sheet/Sheet'

import { DiscardChangesDialog } from './DiscardChangesDialog'
import { SectorExpandableText } from './SectorExpandableText'
import { SelectedElementHeader } from './SelectedElementHeader'
import { SetupCartActions } from './SetupCartActions'
import { SetupProductsList } from './SetupProductsList'
import { VehiclePlate } from './VehiclePlate'

export const SelectedElementDrawer = () => {
  const dispatch = useAppDispatch()

  const cartReservations = useAppSelector(cartSlice.selectors.cartReservations)
  const dayDifference = useAppSelector(dayDifferenceSelector)
  const selectedElement = useAppSelector(mapSelectors.selectedElement)
  const sectorDetails = useAppSelector(mapCurrentSectorDetailsSelector)
  const cartItems = useAppSelector(cartSlice.selectors.items)
  const setupData = useAppSelector(mapSelectedElementSetupDataSelector)
  const selectedElementSetupItems = setupData?.selectedElementSetupItems
  const selectedElementServices = useAppSelector(mapSelectedElementServices)
  const selectedElementQuotableProducts = useAppSelector(
    cartSlice.selectors.selectedElementQuotableProducts
  )
  const activeCartReservation = useAppSelector(selectActiveCartReservation)
  const selectedElementReservation = useAppSelector(
    cartSlice.selectors.selectedElementReservation
  )
  const selectedElementQuotedSeatsAmount = useAppSelector(
    activeElementQuotedSeatsAmountSelector
  )
  const areCartButtonsEnabled = useAppSelector(areDrawerButtonsEnabledSelector)

  const isElementAddedToCart = useMemo(
    () => selectedElement && selectedElement.id in cartItems,
    [cartItems, selectedElement]
  )

  useEffect(() => {
    if (!selectedElement) {
      return
    }

    // check if there is already a reservation for this element
    const foundReservation = cartReservations.find(
      (r) =>
        r.spotType === selectedElement.subType &&
        r.spotName === selectedElement.name
    )

    if (foundReservation) {
      dispatch(
        cartSlice.actions.setSelectedElementQuotableProducts({
          b: foundReservation.beds,
          boughtServices: foundReservation.boughtServices,
          c: foundReservation.chairs,
          d: foundReservation.deckChairs,
          m: foundReservation.maxiBeds,
        })
      )

      return
    }

    const includedServices = selectedElementServices.includedServices
    const extraServices = selectedElementServices.extraServices
    const ticketServices = selectedElementServices.ticketServices

    const selectedElementSeats = [
      MapSetupItemKeyName.BED,
      MapSetupItemKeyName.CHAIR,
      MapSetupItemKeyName.DECK_CHAIR,
      MapSetupItemKeyName.MAXI_BED,
    ].reduce<Omit<QuoteSetupItems, 'services' | 'boughtServices'>>(
      (acc, key) => {
        const setupItem = selectedElementSetupItems?.find(
          (item) => item.keyName === key
        )
        if (!setupItem) return acc

        return {
          ...acc,
          [key]: getSeatDefaultQuantity({
            selectedElementCartReservation: activeCartReservation,
            setupItem: setupItem,
          }),
        }
      },
      { b: 0, c: 0, d: 0, m: 0 }
    )

    const selectedSeats = Object.values(selectedElementSeats).reduce(
      (acc, n) => acc + n,
      0
    )

    const boughtServices = [
      ...includedServices,
      ...extraServices,
      ...ticketServices,
    ].reduce<QuoteReservation['boughtServices']>((acc, service) => {
      const qty = getServiceUpdatedQuantity({
        dayDifference,
        selectedSeats,
        service,
      })

      if (qty === 0 || service.scheduleType !== 'daily') {
        return acc
      }

      switch (service.scheduleType) {
        case 'daily': {
          const days = getDaysFromPriceMode(service, dayDifference)
          if (days.length === 0) {
            return acc
          }

          acc[service.serviceId] = {
            [getTimeScheduleKey(service.timeSchedules[0])]: {
              bought: qty,
              days,
              serviceId: service.serviceId,
              serviceName: service.name,
              timeSchedule: service.timeSchedules[0],
            },
          }
          break
        }

        default: {
          assertNever(service.scheduleType)
        }
      }

      return acc
    }, {})

    dispatch(
      cartSlice.actions.setSelectedElementQuotableProducts({
        ...selectedElementSeats,
        boughtServices,
      })
    )
  }, [])

  const quoteCallback = useCallback(() => {
    if (areCartButtonsEnabled) {
      dispatch(cartSlice.actions.quote())
    }
  }, [dispatch, areCartButtonsEnabled])

  const quote = useDebounceCallback(quoteCallback, 500)

  useEffect(() => {
    dispatch(cartAddReservationToQuote())
    quote()
  }, [
    selectedElement,
    selectedElementQuotableProducts,
    selectedElementQuotedSeatsAmount,
  ])

  const [isDiscardChangesDialogOpen, setIsDiscardChangesDialogOpen] =
    useState(false)

  const handleCloseDrawer = useCallback(() => {
    setOpen(false)
  }, [])

  const handleCloseWithQuote = useCallback((setMapLoading = true) => {
    if (setMapLoading) {
      dispatch(
        mapSlice.actions.setIsMapLoading({
          timestamp: Date.now(),
        })
      )
    }

    dispatch(cartSlice.actions.setSelectedElementReservation(null))
    dispatch(ticketSlice.actions.resetValidation())

    quote()

    setOpen(false)

    // Please note: this timeout is necessary to complete the exit animation
    setTimeout(() => {
      dispatch(mapSlice.actions.selectMapElement(null))

      dispatch(
        cartSlice.actions.setSelectedElementQuotableProducts(
          cartSliceInitialState.selectedElementQuotableProducts
        )
      )

      document.body.style.pointerEvents = 'auto'
    }, 200)
  }, [])

  function onCloseClick() {
    if (!selectedElement) return

    dispatch(ticketSlice.actions.resetValidation())

    const areReservationsEqual = getObjectsComparison(
      activeCartReservation,
      selectedElementReservation || {}
    )

    if (isElementAddedToCart && !areReservationsEqual) {
      setIsDiscardChangesDialogOpen(true)
    } else {
      handleCloseWithQuote(false)
    }
  }

  const [open, setOpen] = useState(true)

  if (!sectorDetails || !selectedElement || !selectedElementSetupItems) {
    return null
  }

  return (
    <Sheet
      onOpenChange={(open) => {
        if (open) {
          return
        }

        onCloseClick()
      }}
      open={open}
    >
      <SheetContent
        className="flex flex-col justify-between gap-0 overflow-hidden pt-4 lg:h-full"
        onCloseAutoFocus={(event) => {
          // https://github.com/radix-ui/primitives/issues/1241
          event.preventDefault()
          document.body.style.pointerEvents = ''
        }}
      >
        <div className="flex flex-grow flex-col justify-start max-lg:overflow-auto  lg:overflow-hidden">
          <SheetTitle className="sr-only">
            <span>{selectedElement?.name}</span>
          </SheetTitle>

          <SheetHeader className="relative z-10 w-full lg:sticky lg:top-0">
            <SelectedElementHeader />

            <DiscardChangesDialog
              handleClose={handleCloseWithQuote}
              isOpen={isDiscardChangesDialogOpen}
              setIsOpen={setIsDiscardChangesDialogOpen}
            />
          </SheetHeader>

          <div className="w-full lg:overflow-auto">
            <SectorExpandableText />

            {selectedElementReservation !== null ? (
              <VehiclePlate
                selectedElement={selectedElement}
                selectedElementReservation={selectedElementReservation}
              />
            ) : null}
            <SetupProductsList />
          </div>

          <SheetFooter className="sticky bottom-0 left-0 right-0 z-10 mt-auto flex bg-white">
            <SetupCartActions
              handleCloseDrawer={handleCloseDrawer}
              handleCloseWithQuote={handleCloseWithQuote}
            />
          </SheetFooter>
        </div>
      </SheetContent>
    </Sheet>
  )
}
